import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { ListGroup, Collapse, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { VscSymbolString } from 'react-icons/vsc';
import { FaPlus, FaMinus } from 'react-icons/fa';
import ElementDetailsModal from '../../modals/SubmodelElementDetailsModal';
import { useIntl } from 'react-intl';
import Config from '../../../config/Config';
import getLocalizedText from '../../Utils';

const DefaultSubmodelElementsRenderer = ({ element }) => {
  const { mainColor, secondaryColor } = Config.appearance;
  const truncateLength = 35;
  const intl = useIntl();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedElement, setSelectedElement] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const {
    idShort,
    modelType,
    entityType = '',
    submodelElements,
    displayName,
    value,
    statements = [],
    globalAssetId,
  } = element || {};

  const isCollapsibleCollection =
    modelType === 'SubmodelElementCollection' || modelType === 'SubmodelElementList';

  const isCollapsibleEntity =
    modelType === 'Entity' &&
    (entityType === 'SelfManagedEntity' || entityType === 'CoManagedEntity') &&
    statements.some(
      (stmt) =>
        stmt.modelType === 'RelationshipElement' &&
        stmt.semanticId?.keys?.[0]?.value?.includes('HasPart')
    );

  const isCollapsible = isCollapsibleCollection || isCollapsibleEntity;

  const toggleOpen = () => setIsOpen((prev) => !prev);
  const openDetails = (el) => {
    setSelectedElement(el);
    setShowModal(true);
  };

  // Reusable display helper: handles localization + truncation + styling
  const getDisplayString = useCallback(
    (val, fallback = 'N/A', options = {}) => {
      const {
        bold = false,
        color = null,
        truncate = true,
        asDiv = false,
      } = options;

      const text = getLocalizedText(val, fallback, intl.locale);

      if (!text || text === fallback) return fallback;

      let content = text;

      // Truncate with tooltip
      if (truncate && typeof text === 'string' && text.length > truncateLength) {
        content = (
          <OverlayTrigger placement="auto" overlay={<Tooltip>{text}</Tooltip>}>
            <span>{text.substring(0, truncateLength)}...</span>
          </OverlayTrigger>
        );
      }

      const styled = bold || color ? (
        <span style={{
            fontWeight: bold ? 'bold' : 'normal',
            color: color || 'inherit',
        }}>
          {content}
        </span>
      ) : (
        content
      );

      return asDiv ? <div>{styled}</div> : styled;
    },
    [intl.locale]
  );

  // Helper for values (bold + mainColor)
  const renderValue = useCallback(
    (val) => getDisplayString(val, null, { bold: true, color: mainColor }),
    [getDisplayString, mainColor]
  );

  // Helper for displayName (with truncation)
  const renderDisplayName = useCallback(
    (val, fallback = 'N/A') => getDisplayString(val, fallback, { truncate: true }),
    [getDisplayString]
  );

  const getChildren = () => {
    if (submodelElements) 
      return submodelElements;
    if (modelType === 'Entity' && (entityType === 'SelfManagedEntity' || entityType === 'CoManagedEntity')) 
      return [...statements];
    return [];
  };

  const children = getChildren();

  const headerLabel =
    modelType === 'SubmodelElementCollection' || modelType === 'Entity'
      ? renderDisplayName(displayName, 'N/A')
      : idShort || 'N/A';

  const CollapsibleHeader = () => (
    <ListGroup.Item
      onClick={toggleOpen}
      style={{
        cursor: 'pointer',
        borderRadius: '20px',
        padding: '14px 16px',
        border: 0,
        backgroundColor: secondaryColor,
        minHeight: '56px',
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          width: '22px',
          height: '22px',
          borderRadius: '50%',
          backgroundColor: mainColor,
        }}>
          {isOpen ? (
            <FaMinus style={{ color: '#fff', fontSize: '10px' }} />
          ) : (
            <FaPlus style={{ color: '#fff', fontSize: '10px' }} />
          )}
        </div>
        <div>
          <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#333' }}>
            {headerLabel}
          </span>
          {idShort && (
            <>
              <br />
              <span style={{ fontSize: '12px', color: '#666', display: 'inline-flex', alignItems: 'center' }}>
                <VscSymbolString style={{ fontSize: '14px', marginRight: '3px', flexShrink: 0 }} />
                {idShort}
              </span>
            </>
          )}
        </div>
      </div>
    </ListGroup.Item>
  );

  const PropElement = () => {
    const assetIdValue =
      typeof globalAssetId === 'string'
        ? globalAssetId
        : globalAssetId?.keys?.[0]?.value ||
          globalAssetId?.value ||
          '—';

    const valueContent =
      modelType === 'Entity' && globalAssetId ? (
        <b style={{ color: mainColor }}>
          {getDisplayString(assetIdValue, '—', { truncate: true })}
        </b>
      ) : value != null ? (
        renderValue(value)
      ) : null;

    return (
      <ListGroup.Item
        className="mt-2 list-group-item-action"
        onClick={() => openDetails(element)}
        style={{
          cursor: 'pointer',
          borderRadius: '20px',
          border: 0,
          backgroundColor: '#FFFFFF',
          padding: '14px 16px',
          fontSize: '16px',
          minHeight: '56px',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start gap-1">
          <div style={{ minWidth: 0 }}>
            {renderDisplayName(displayName, 'N/A')}
            {idShort && (
              <>
                <br />
                <span style={{ fontSize: '12px', color: '#666' }}>
                  <VscSymbolString
                    style={{ fontSize: '14px', marginRight: '3px', position: 'relative', top: '-1px' }}
                  />
                  {idShort}
                </span>
              </>
            )}
          </div>
          {valueContent && (
            <div
              className="text-sm-end"
              style={{ flexShrink: 0, maxWidth: '100%', wordBreak: 'break-word' }}
            >
              {valueContent}
            </div>
          )}
        </div>
      </ListGroup.Item>
    );
  };

  return (
    <>
      {isCollapsible ? (
        <ListGroup className="list-group-flush mt-3 mb-1">
          <CollapsibleHeader />
          <Collapse in={isOpen}>
            <div>
              <ListGroup className="ms-3">
                {children.map((child) => (
                  <DefaultSubmodelElementsRenderer
                    key={child.idShort || child.id || JSON.stringify(child)}
                    element={child}
                  />
                ))}
                {Array.isArray(value) &&
                  modelType !== 'MultiLanguageProperty' &&
                  value.map((item, idx) => (
                    <DefaultSubmodelElementsRenderer key={idx} element={item} />
                  ))}
              </ListGroup>
            </div>
          </Collapse>
        </ListGroup>
      ) : (
        <ListGroup className="ms-3">
          <PropElement />
          {children.map((child) => (
            <DefaultSubmodelElementsRenderer
              key={child.idShort || child.id || JSON.stringify(child)}
              element={child}
            />
          ))}
          {Array.isArray(value) &&
            modelType !== 'MultiLanguageProperty' &&
            value.map((item, idx) => (
              <DefaultSubmodelElementsRenderer key={idx} element={item} />
            ))}
        </ListGroup>
      )}

      {selectedElement && (
        <ElementDetailsModal
          show={showModal}
          onHide={() => setShowModal(false)}
          element={selectedElement}
        />
      )}
    </>
  );
};

DefaultSubmodelElementsRenderer.propTypes = {
  element: PropTypes.shape({
    idShort: PropTypes.string,
    modelType: PropTypes.string,
    entityType: PropTypes.string,
    submodelElements: PropTypes.array,
    statements: PropTypes.array,
    displayName: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    value: PropTypes.any,
    globalAssetId: PropTypes.any,
  }).isRequired,
};

export default DefaultSubmodelElementsRenderer;